import { Link as GatsbyLink, navigate } from "gatsby";
import PropTypes from "prop-types";
import React, { useState } from "react";
import styled from "@emotion/styled";
import { Global, css } from "@emotion/react";
import { assignWith, isEmpty, isNull, isUndefined } from "lodash";

import withTranslationProps from "~/hoc/withTranslationProps";
import { translateGatsbyLink } from "~/translators/functions";

const AnchorLink = styled.a``;

const GatsbyJumpLink = ({ onClick, href, tagRef, ...rest }) => {
    const [smoothScroll, setSmoothScroll] = useState(false);
    return (
        <>
            {smoothScroll ? (
                <Global
                    styles={css`
                        html {
                            scroll-behavior: smooth;
                        }
                    `}
                />
            ) : null}
            <AnchorLink
                {...rest}
                href={href}
                ref={tagRef}
                onClick={(event) => {
                    event.preventDefault();
                    setSmoothScroll(true);
                    navigate(href, {
                        state: { disableScrollUpdate: true },
                    });
                    if (onClick) {
                        onClick(event);
                    }
                }}
            />
        </>
    );
};

const isRelativeLink = (to) => {
    return new RegExp(`^(/(?!/))`).test(to);
};

const isHashLink = (to) => {
    return /(#[a-zA-Z0-9_=-]*)/.test(to);
};

const shouldOpenInNewTab = ({ to, external }) => {
    return external || !isRelativeLink(to);
};

const shouldUseGatsbyJumpLink = (props) => {
    const { to } = props;

    return !shouldOpenInNewTab(props) && isHashLink(to);
};

const shouldUseGatsbyLink = (props) => {
    const { to } = props;

    if (shouldOpenInNewTab(props)) {
        return false;
    }

    return isRelativeLink(to) && !isHashLink(to);
};

const formatProps = (props, translateGatsbyLink) => {
    const {
        ariaLabel,
        ariaExpanded,
        className,
        navIndex,
        tagLabel,
        tagRef,
        to,
        onClick,
        onKeyDown,
        state,
        isSubNav,
    } = props;

    const genericProps = { className, onKeyDown, onClick };

    if (shouldUseGatsbyJumpLink(props)) {
        genericProps.tagRef = tagRef;
    } else {
        if (tagRef && isSubNav) {
            genericProps.ref = (el) => {
                tagRef.current[navIndex] = el;
            };
        } else {
            genericProps.ref = tagRef;
        }
    }

    if (ariaLabel) {
        genericProps["aria-label"] = ariaLabel;
    }

    if (!isNull(ariaExpanded)) {
        genericProps["aria-expanded"] = ariaExpanded;
    }

    if (!isNull(tagLabel)) {
        genericProps["data-label"] = tagLabel;
    }

    if (shouldUseGatsbyLink(props)) {
        return { to: translateGatsbyLink(to), state, ...genericProps };
    } else {
        const newTabAttributes = {};
        if (shouldOpenInNewTab(props)) {
            newTabAttributes.target = "_blank";
            newTabAttributes.rel = "noopener noreferrer";
        }
        return {
            href: translateGatsbyLink(to),
            ...newTabAttributes,
            ...genericProps,
        };
    }
};

const customizer = (objValue, srcValue) => {
    if (!isUndefined(objValue)) {
        return objValue;
    }

    return srcValue;
};

const defaultProps = {
    ariaLabel: null,
    ariaExpanded: null,
    className: "",
    external: false,
    onClick: () => {},
    onKeyDown: null,
    tagLabel: null,
    tagRef: null,
    navIndex: -1,
    state: {},
    isSubNav: false,
};

const BaseLink = ({ children, translationProps, ...rest }) => {
    if (isEmpty(children) || isEmpty(rest.to)) {
        return null;
    }

    const props = assignWith({}, rest, defaultProps, customizer);

    const formattedProps = formatProps(props, (link) => {
        return translateGatsbyLink(link, translationProps);
    });

    let Link;

    if (shouldUseGatsbyLink(props)) {
        Link = GatsbyLink;
    } else if (shouldUseGatsbyJumpLink(props)) {
        Link = GatsbyJumpLink;
    } else {
        Link = AnchorLink;
    }

    return <Link {...formattedProps}>{children}</Link>;
};

BaseLink.propTypes = {
    children: PropTypes.node, // Children prop is Required for link to show
    to: PropTypes.string, // To prop is Required for link to show
    ariaLabel: PropTypes.string,
    ariaExpanded: PropTypes.bool,
    className: PropTypes.string,
    external: PropTypes.bool,
    navIndex: PropTypes.number,
    tagLabel: PropTypes.string,
    tagRef: PropTypes.object,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    state: PropTypes.object,
    isSubNav: PropTypes.bool,
};

export default withTranslationProps(BaseLink);
